import Link from "next/link";
import Image from "next/image";
import { Article, ArticleCategory } from "@/data/articles";
import { CalendarDays } from "lucide-react";

// ─── Category colour map ───────────────────────────────────────────────────
const CATEGORY_STYLES: Record<string, string> = {
  "Naturopathic Medicine": "bg-emerald-100 text-emerald-800 border-emerald-200",
  "Herbal Medicine": "bg-lime-100 text-lime-800 border-lime-200",
  Nutrition: "bg-amber-100 text-amber-800 border-amber-200",
  "Mental Health": "bg-sky-100 text-sky-800 border-sky-200",
};

function getCategoryStyle(category: ArticleCategory): string {
  return (
    CATEGORY_STYLES[category] ??
    "bg-healing-100 text-healing-800 border-healing-200"
  );
}

// ─── Types ─────────────────────────────────────────────────────────────────
interface ArticleCardProps {
  article: Article;
  variant?: "default" | "featured";
}

// ─── Sub-components ────────────────────────────────────────────────────────
function CategoryBadge({ category }: { category: ArticleCategory }) {
  return (
    <span
      className={[
        "inline-block text-[11px] font-semibold uppercase tracking-widest",
        "px-2.5 py-1 rounded-full border",
        getCategoryStyle(category),
      ].join(" ")}
    >
      {category}
    </span>
  );
}

function FeaturedBadge() {
  return (
    <span className="inline-block text-[11px] font-bold uppercase tracking-widest px-2.5 py-1 rounded-full bg-amber-400 text-amber-900 border border-amber-500">
      ★ Featured
    </span>
  );
}

function ArticleDate({ date }: { date: Article["date"] }) {
  return (
    <time
      dateTime={`${date.year}-${date.month}-${date.day}`}
      className="flex items-center gap-1.5 text-xs text-muted-foreground"
    >
      <CalendarDays className="h-3.5 w-3.5" />
      {date.day} {date.month} {date.year}
    </time>
  );
}

// ─── Main component ────────────────────────────────────────────────────────
export function ArticleCard({
  article,
  variant = "default",
}: ArticleCardProps) {
  const isFeatured = variant === "featured";
  const imageSrc = `${article.thumbnail}`;

  return (
    <Link
      href={`/articles/${article.slug}`}
      className={[
        "group flex flex-col bg-white rounded-2xl overflow-hidden no-underline",
        "border border-healing-100 shadow-sm",
        "transition-all duration-300 hover:shadow-md hover:-translate-y-1",
        isFeatured ? "ring-2 ring-amber-300 ring-offset-2" : "",
      ].join(" ")}
    >
      {/* Thumbnail */}
      <div
        className={[
          "relative overflow-hidden w-full",
          isFeatured ? "h-56" : "h-48",
        ].join(" ")}
      >
        <Image
          src={imageSrc}
          alt={article.title}
          fill
          sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
          className="object-cover transition-transform duration-500 group-hover:scale-105"
        />
        {/* Gradient overlay */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent" />

        {/* Badges overlaid on image */}
        <div className="absolute top-3 left-3 flex flex-wrap gap-1.5">
          {isFeatured && <FeaturedBadge />}
          <CategoryBadge category={article.category} />
        </div>
      </div>

      {/* Body */}
      <div className="flex flex-col flex-1 p-5 gap-3">
        <h2
          className={[
            "font-serif-display text-healing-900 leading-snug",
            "group-hover:text-healing-700 transition-colors",
            isFeatured ? "text-xl" : "text-lg",
          ].join(" ")}
        >
          {article.title}
        </h2>

        <p className="text-sm text-muted-foreground leading-relaxed line-clamp-3 flex-1">
          {article.excerpt}
        </p>

        {/* Footer */}
        <div className="flex items-center justify-between pt-3 border-t border-healing-50 mt-auto">
          <ArticleDate date={article.date} />
          <span className="text-xs font-semibold text-healing-700 group-hover:text-healing-900 transition-colors">
            Read more →
          </span>
        </div>
      </div>
    </Link>
  );
}
